import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import { SignInOwner } from 'graphql/user';
import { ErrorString, showGraphQLError, setTokenFromSignInOwner, getToken, unlockNotificationSound } from 'shared/Common';
import { validatePassword, validateEmail, validateId } from 'utils/validator';
import SettingPrinter from './SettingPrinter';
import Modal from 'components/Modal';
import ShopList from 'screens/ShopList';
import { readSettingFile, writeSettingFile } from 'shared/Settings';

let modalInner;
let modalTitle = '';

// 1. 키오브릭 SVG 로고 컴포넌트
function KiobricLogo({ className = "w-10 h-10" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" className={className}>
      <defs>
        <linearGradient id="kiobricGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4F46E5" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>
        <linearGradient id="screenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <rect x="45" y="30" width="110" height="140" rx="16" fill="url(#kiobricGrad)" />
      <rect x="57" y="42" width="86" height="84" rx="8" fill="#FFFFFF" opacity="0.95" />
      <rect x="65" y="52" width="70" height="8" rx="4" fill="url(#screenGrad)" opacity="0.8" />
      <rect x="65" y="66" width="32" height="26" rx="5" fill="url(#screenGrad)" opacity="0.2" />
      <rect x="103" y="66" width="32" height="26" rx="5" fill="url(#screenGrad)" opacity="0.2" />
      <rect x="65" y="98" width="70" height="18" rx="5" fill="url(#screenGrad)" />
      <path d="M93 107 L98 112 L108 102" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="75" y="136" width="50" height="6" rx="3" fill="#FFFFFF" opacity="0.4" />
      <path d="M83 142 L117 142 L117 154 L112 150 L107 154 L102 150 L97 154 L92 150 L87 154 L83 150 Z" fill="#FFFFFF" opacity="0.9" />
    </svg>
  );
}

export default function Login(props) {
  const history = useHistory();
  const [visibleSettingPrinter, visibleSettingPrinterSet] = useState(false);

  const [userId, userIdSet] = useState('');
  const [userPwd, userPwdSet] = useState('');

  const [autoLoginChecked, autoLoginCheckedSet] = useState(() => {
    const initSettings = readSettingFile();
    return initSettings.autoLogin || false;
  });

  const [signInOwner] = useMutation(SignInOwner);

  useEffect(() => {
    const checkAutoLogin = async () => {
      const settings = readSettingFile();
      const token = getToken();

      if (settings.autoLogin && token) {
        history.replace('/main');
      }
    };

    checkAutoLogin();
  }, [history]);

  function openSettingPrinter() {
    modalTitle = '설정';
    modalInner = (
      <SettingPrinter
        onClose={() => {
          visibleSettingPrinterSet(false);
          const latestSettings = readSettingFile();
          autoLoginCheckedSet(latestSettings.autoLogin || false);
        }}
      />
    );
    visibleSettingPrinterSet(true);
  }

  const validateInputField = (isNeedFocus) => {
    let errMsg = '';
    if (!validateId(userId)) {
      errMsg = ErrorString.CheckEmailFormat;
    } else if (!validatePassword(userPwd)) {
      errMsg = ErrorString.CheckPasswordFormat;
    } else {
      return true;
    }
    alert(errMsg);
    return false;
  };

  const signInButtonPressed = async (e) => {
    if (e) e.preventDefault();
    unlockNotificationSound();

    if (process.env.NODE_ENV !== 'development' && !validateInputField(true)) {
      return;
    }

    try {
      const { data } = await signInOwner({ variables: { ownerId: userId, ownerPwd: userPwd } });
      if (data && data.signInOwner) {
        setTokenFromSignInOwner(data.signInOwner);

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.scrollTo(0, 0);

        const currentSettings = readSettingFile();
        currentSettings.autoLogin = autoLoginChecked;
        await writeSettingFile(currentSettings);

        modalTitle = '가게 목록';
        modalInner = <ShopList />;
        visibleSettingPrinterSet(true);
      }
    } catch (error) {
      showGraphQLError(error);
    }
  };

  return (
    <div className="flex bg-cust-gray min-h-screen justify-center items-center px-4 py-8">
      <div className="w-full sm:w-3/4 md:w-1/2 lg:max-w-md xl:max-w-lg">
        
        {/* 2. 로그인 폼 상단 브랜딩 헤더 영역 */}
        <div className="flex flex-col items-center mb-8">
          <KiobricLogo className="w-16 h-16 md:w-20 md:h-20 mb-2 drop-shadow-sm" />
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 tracking-tight flex items-center">
            키오브릭
            <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2.5 py-1 rounded-full ml-2">
              사장님
            </span>
          </h1>
        </div>

        <form onSubmit={signInButtonPressed}>
          <div className="mb-2">
            <input
              className="bg-white h-12 md:h-16 w-full border border-cust-line focus:border-primary placeholder-cust-line text-base md:text-lg pl-4 rounded outline-none"
              type="text"
              placeholder="사장님 아이디"
              onChange={(e) => userIdSet(e.target.value)}
              value={userId}
            />
          </div>
          <div className="mb-4">
            <input
              className="bg-white h-12 md:h-16 w-full border border-cust-line focus:border-primary placeholder-cust-line text-base md:text-lg pl-4 rounded outline-none"
              type="password"
              placeholder="사장님 비밀번호"
              onChange={(e) => userPwdSet(e.target.value)}
              value={userPwd}
            />
          </div>

          <div className="flex flex-row space justify-between items-center mb-6">
            <label className="inline-flex items-center cursor-pointer select-none">
              <input
                className="form-checkbox h-5 w-5 text-primary mr-2 leading-tight box"
                type="checkbox"
                onChange={async () => {
                  const currentSettings = readSettingFile();
                  const nextAutoLoginState = !autoLoginChecked;

                  currentSettings.autoLogin = nextAutoLoginState;
                  await writeSettingFile(currentSettings);
                  autoLoginCheckedSet(nextAutoLoginState);
                }}
                checked={autoLoginChecked}
              />
              <span className="text-sm md:text-base font-bold">자동로그인</span>
            </label>
            <button
              type="button"
              className="text-sm md:text-base font-bold text-gray-700 hover:underline"
              onClick={() => {
                alert(
                  `사장님 앱에서 아이디/비밀번호를 찾아주세요.\n\n사장님앱은\n구글/애플 앱스토어에서 '키오브릭'로 검색해주세요.`,
                );
              }}>
              아이디/비밀번호 찾기
            </button>
          </div>

          <button 
            className="w-full h-12 md:h-16 mb-4 text-white bg-primary text-base md:text-lg font-bold rounded hover:opacity-90 transition-opacity" 
            type="submit"
          >
            확인
          </button>

          <div className="text-center">
            <button 
              className="h-10 md:h-12 mb-4 font-bold text-sm md:text-base text-gray-600 hover:text-black" 
              type="button" 
              onClick={() => openSettingPrinter()}
            >
              인쇄설정
            </button>
          </div>

          <Modal
            hasCloseBtn
            title={modalTitle}
            isOpen={visibleSettingPrinter}
            onRequestClose={() => visibleSettingPrinterSet(false)}>
            {modalInner && modalInner}
          </Modal>
        </form>
      </div>
    </div>
  );
}